import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../../lib/api";
import { SectionCard } from "../Ui";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { FormActions, FormSection } from "../forms/FormField";
import { DEFAULT_PADDY_LAB_VALUES, PaddyLabAnalysisFields } from "../forms/PaddyLabAnalysisFields";
import { PartySelect } from "../forms/PartySelect";
import { PurchasePendingSelect } from "../forms/PurchasePendingSelect";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, hasPermission } from "../../context/AuthContext";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import LabTabShell from "./LabTabShell";
import PaddyLabReportRegister from "./PaddyLabReportRegister";
import SampleNotReceivedDialog from "./SampleNotReceivedDialog";
import { mapNumberFields, PADDY_LAB_NUMBER_KEYS } from "../../lib/numberInput";
import { PADDY_LAB_METRICS } from "../../lib/paddyLabConfig";

function reportToForm(report) {
  if (!report) return { ...DEFAULT_PADDY_LAB_VALUES };
  return {
    ...DEFAULT_PADDY_LAB_VALUES,
    moisture_pct: report.moisture_pct ?? "",
    yield_pct: report.yield_pct ?? "",
    broken_pct: report.broken_pct ?? "",
    damage_pct: report.damage_pct ?? "",
    green_pct: report.green_pct ?? "",
    immature_pct: report.immature_pct ?? "",
    admixture_pct: report.admixture_pct ?? "",
    pr_pct: report.pr_pct ?? "",
    length_mm: report.length_mm ?? "",
    choba_pct: report.choba_pct ?? "",
    kachri_pct: report.kachri_pct ?? "",
    custom_param_1_name: report.custom_param_1_name || "",
    custom_param_1_value: report.custom_param_1_value || "",
    custom_param_2_name: report.custom_param_2_name || "",
    custom_param_2_value: report.custom_param_2_value || "",
    notes: report.notes || "",
  };
}

export function TruckLabTab() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, reload } = usePageBootstrap({ path: "/lab/bootstrap", params: { tab: "truck" }, wsTypes: ["lab", "purchase"] });
  useBootstrapWS(["lab", "purchase"], reload);

  const parties = data?.parties || [];
  const queue = data?.queue || [];
  const correctionQueue = data?.correction_queue || [];
  const reports = data?.reports || [];
  const correctionCount = data?.correction_count ?? correctionQueue.length;
  const [partyId, setPartyId] = useState("");
  const [purchaseId, setPurchaseId] = useState(() => searchParams.get("purchase") || "");
  const [form, setForm] = useState({ ...DEFAULT_PADDY_LAB_VALUES });
  const [submitting, setSubmitting] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [waivingId, setWaivingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const selectablePurchases = useMemo(() => {
    const byId = new Map();
    for (const p of [...correctionQueue, ...queue]) {
      byId.set(String(p.id), p);
    }
    return Array.from(byId.values());
  }, [queue, correctionQueue]);

  const selectedPurchase = selectablePurchases.find((p) => String(p.id) === String(purchaseId));
  const selectedReport = reports.find(
    (r) => String(r.purchase_id) === String(purchaseId) && (r.sample_phase !== "RELAB" || correcting),
  );
  const canMarkSampleNotReceived = Boolean(
    selectedPurchase
    && (
      queue.some((p) => String(p.id) === String(selectedPurchase.id))
      || correctionQueue.some((p) => String(p.id) === String(selectedPurchase.id))
    ),
  );

  const onPartyChange = (id) => {
    setPartyId(id);
    setPurchaseId("");
    setCorrecting(false);
    setForm({ ...DEFAULT_PADDY_LAB_VALUES });
  };

  const selectCorrection = (p) => {
    if (p?.party_id) setPartyId(String(p.party_id));
    setPurchaseId(String(p.id));
    setCorrecting(true);
    const existing = reports.find((r) => String(r.purchase_id) === String(p.id) && r.sample_phase !== "RELAB")
      || reports.find((r) => String(r.purchase_id) === String(p.id));
    setForm(reportToForm(existing));
  };

  const selectQueuePurchase = (p) => {
    if (p?.party_id) setPartyId(String(p.party_id));
    setPurchaseId(String(p.id));
    setCorrecting(false);
    setForm({ ...DEFAULT_PADDY_LAB_VALUES });
  };

  useEffect(() => {
    const fromUrl = searchParams.get("purchase");
    if (!fromUrl) return;
    const match = selectablePurchases.find((p) => String(p.id) === String(fromUrl));
    if (!match) {
      if (String(purchaseId) !== String(fromUrl)) setPurchaseId(String(fromUrl));
      return;
    }
    if (String(purchaseId) === String(match.id) && (partyId || !match.party_id)) return;
    if (correctionQueue.some((p) => String(p.id) === String(match.id))) {
      selectCorrection(match);
    } else {
      selectQueuePurchase(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply deep-link once queue loads
  }, [searchParams, selectablePurchases, correctionQueue]);

  useEffect(() => {
    const current = searchParams.get("purchase") || "";
    if (String(purchaseId || "") === current) return;
    const next = new URLSearchParams(searchParams);
    if (purchaseId) next.set("purchase", String(purchaseId));
    else next.delete("purchase");
    setSearchParams(next, { replace: true });
  }, [purchaseId]);

  const requestSampleNotReceived = (purchase) => {
    const target = purchase || selectedPurchase;
    if (!target?.id) return toast.error("Select a purchase");
    setConfirmTarget(target);
  };

  const markSampleNotReceived = async () => {
    const target = confirmTarget;
    if (!target?.id) return;
    setWaivingId(target.id);
    try {
      await api.post("/lab-reports/sample-not-received", { purchase_id: +target.id });
      toast.success("Marked sample not received — no truck lab report");
      setConfirmTarget(null);
      if (String(purchaseId) === String(target.id)) {
        setPurchaseId("");
        setCorrecting(false);
        setForm({ ...DEFAULT_PADDY_LAB_VALUES });
      }
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setWaivingId(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!purchaseId) return toast.error("Select a purchase");
    setSubmitting(true);
    try {
      const saved = await api.post("/lab-reports", {
        purchase_id: +purchaseId,
        ...mapNumberFields(form, PADDY_LAB_NUMBER_KEYS),
      });
      if (saved?.validation_pending) {
        const labels = (saved.out_of_range || []).map(
          (key) => PADDY_LAB_METRICS.find((m) => m.key === key)?.label || key,
        );
        toast.warning(
          labels.length
            ? `Saved, but needs correction: ${labels.join(", ")}. Fix values and re-submit.`
            : "Saved, but values are outside allowed range — open Needs correction to fix.",
        );
      } else {
        toast.success(correcting ? "Corrected lab results saved" : "Truck lab results recorded");
      }
      setPurchaseId("");
      setCorrecting(false);
      setForm({ ...DEFAULT_PADDY_LAB_VALUES });
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const record = (
    <SectionCard title="Truck lab (incoming grain)">
      {queue.length > 0 && (
        <div
          className="mb-4 rounded-md border border-border bg-muted/20 px-3 py-3"
          data-testid="truck-lab-awaiting-queue"
        >
          <p className="text-sm font-medium text-foreground">
            Awaiting truck sample ({queue.length})
          </p>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Click a row to enter readings, or mark sample not received if no sample arrived.
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {queue.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2"
                data-testid={`truck-queue-${p.code}`}
              >
                <button
                  type="button"
                  onClick={() => selectQueuePurchase(p)}
                  className="min-w-0 flex-1 text-left flex flex-wrap items-center gap-2 hover:opacity-80"
                >
                  {p.vehicle_number && (
                    <span className="text-xs font-medium uppercase">{p.vehicle_number}</span>
                  )}
                  <span className="font-mono text-xs">{p.code}</span>
                  <span className="text-sm font-medium">{p.party_name}</span>
                </button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs shrink-0"
                  disabled={waivingId === p.id}
                  onClick={() => requestSampleNotReceived(p)}
                  data-testid={`truck-sample-not-received-${p.code}`}
                >
                  {waivingId === p.id ? "Saving…" : "Sample not received"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {correctionQueue.length > 0 && (
        <div
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-3"
          data-testid="truck-lab-correction-queue"
        >
          <p className="text-sm font-medium text-amber-950">
            Needs correction ({correctionQueue.length})
          </p>
          <p className="text-xs text-amber-900/80 mt-1 mb-2">
            These reports were saved with values outside the allowed lab range. Fix readings, or mark sample not received.
          </p>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {correctionQueue.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-card/70 px-3 py-2"
                data-testid={`truck-correction-${p.code}`}
              >
                <button
                  type="button"
                  onClick={() => selectCorrection(p)}
                  className="min-w-0 flex-1 text-left flex flex-wrap items-center gap-2 hover:opacity-80"
                >
                  {p.vehicle_number && (
                    <span className="text-xs font-medium uppercase">{p.vehicle_number}</span>
                  )}
                  <span className="font-mono text-xs">{p.code}</span>
                  <span className="text-sm font-medium">{p.party_name}</span>
                </button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs shrink-0"
                  disabled={waivingId === p.id}
                  onClick={() => requestSampleNotReceived(p)}
                  data-testid={`truck-correction-sample-not-received-${p.code}`}
                >
                  {waivingId === p.id ? "Saving…" : "Sample not received"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-sm text-muted-foreground mb-4">
        Purchases marked <strong>Lab required</strong> at weighbridge appear here after gross weigh.
        Filter by party, then select the purchase and enter quality readings.{" "}
        {hasPermission(user, "page.weighbridge") && (
          <Link to="/weighbridge/inward" className="link-brand font-medium">
            Weighbridge
          </Link>
        )}
        {hasPermission(user, "page.weighbridge") && hasPermission(user, "page.purchases") && " · "}
        {hasPermission(user, "page.purchases") && (
          <Link to="/purchases" className="link-brand font-medium">
            Purchases
          </Link>
        )}
      </p>
      {correcting && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-950">
          Correcting flagged report
          {selectedReport?.out_of_range?.length
            ? `: ${(selectedReport.out_of_range || []).map(
              (key) => PADDY_LAB_METRICS.find((m) => m.key === key)?.label || key,
            ).join(", ")}`
            : ""}
          . Fix the highlighted parameters and submit again.
        </div>
      )}
      <form onSubmit={submit} data-testid="lab-form">
        <FormSection title="Selection">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PartySelect
              parties={parties}
              value={partyId}
              onChange={onPartyChange}
              required
              disabled={loading}
              testId="truck-lab-party"
            />
            <PurchasePendingSelect
              purchases={selectablePurchases}
              partyId={partyId}
              value={purchaseId}
              onChange={(id) => {
                setPurchaseId(id);
                const fromCorrection = correctionQueue.some((p) => String(p.id) === String(id));
                setCorrecting(fromCorrection);
                if (fromCorrection) {
                  const existing = reports.find((r) => String(r.purchase_id) === String(id) && r.sample_phase !== "RELAB")
                    || reports.find((r) => String(r.purchase_id) === String(id));
                  setForm(reportToForm(existing));
                } else {
                  setForm({ ...DEFAULT_PADDY_LAB_VALUES });
                }
              }}
              requireParty
            />
          </div>
          {selectedPurchase && (
            <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
              {selectedPurchase.vehicle_number && (
                <span>
                  <strong>Vehicle:</strong> {String(selectedPurchase.vehicle_number).toUpperCase()}
                </span>
              )}
              {selectedPurchase.gate_pass_code && (
                <span>
                  <strong>Gate pass:</strong> {selectedPurchase.gate_pass_code}
                </span>
              )}
              <span>
                <strong>Party:</strong> {selectedPurchase.party_name}
              </span>
            </div>
          )}
        </FormSection>

        <FormSection title="Paddy quality analysis" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <PaddyLabAnalysisFields values={form} onChange={setForm} testIdPrefix="lab" />
          </div>
        </FormSection>

        <FormActions>
          <Button
            type="button"
            variant="outline"
            disabled={submitting || waivingId != null || !canMarkSampleNotReceived}
            onClick={() => requestSampleNotReceived(selectedPurchase)}
            data-testid="lab-sample-not-received"
          >
            {waivingId && String(waivingId) === String(purchaseId) ? "Saving…" : "Sample not received"}
          </Button>
          <Button
            type="submit"
            disabled={submitting || !purchaseId}
            className="bg-bansal-sage hover:bg-bansal-sageDark text-white sm:min-w-[160px]"
            data-testid="lab-submit"
          >
            {submitting ? "Submitting…" : correcting ? "Re-submit corrected report" : "Submit & route"}
          </Button>
        </FormActions>
      </form>
    </SectionCard>
  );

  const register = (
    <PaddyLabReportRegister
      title={`Truck lab register (${reports.length})`}
      reports={reports}
      kind="truck"
      testId="truck-lab-register"
      onReload={reload}
    />
  );

  return (
    <>
      <LabTabShell
        record={record}
        register={register}
        registerCount={reports.length}
        correctionCount={correctionCount}
        testId="truck-lab-tabs"
      />
      <SampleNotReceivedDialog
        open={Boolean(confirmTarget)}
        purchase={confirmTarget}
        kind="truck"
        confirming={waivingId != null}
        onCancel={() => !waivingId && setConfirmTarget(null)}
        onConfirm={markSampleNotReceived}
      />
    </>
  );
}
